// DTOs mirrored from Scout.Deals/Corrections.cs. Keep in step by hand.
import type { Lesson, Proposal, Rule } from './improve'
import type { Scope } from './orgs'

/** Everything Scout asserts about the world from reading mail — and so everything that can be wrong.
 *  One kind per assertion the app renders; the server owns the presets for each (CorrectionPresets). */
export type CorrectionEntityKind =
  | 'lead' | 'opportunity' | 'lifecycle' | 'next-step'
  | 'company-kind' | 'company-segment' | 'account-parent' | 'person-company'
  | 'contact-role' | 'contact-persona'
  | 'threat' | 'meeting-account' | 'signal' | 'account-health'
  | 'rule' | 'manual' | 'play'
  | 'outreach-reason' | 'voice-scenario' | 'research' | 'voice-edit'
  | 'relationship'

/** Where a research note stands after a person judged it (ResearchNoteStatus). Every note is WRITTEN
 *  current; a correction moves it, and a re-run writes a current one over it. */
export type ResearchNoteStatus = 'current' | 'rejected' | 'stale'

/** The entity id a `research` correction carries: the note's own key, prefixed with which note it is. */
export const researchPersonId = (personId: string) => `person:${personId}`
export const researchCompanyId = (engagementId: string) => `company:${engagementId}`

/** open until the nightly loop reads it; then improved, or needs-human when it could not generalise. */
export type CorrectionStatus = 'open' | 'improved' | 'needs-human' | 'declined'

/** 'rejected' withdraws the claim; anything else is what the person says it is instead. */
export const REJECTED = 'rejected'

/** The shortest reason the API will accept. The reason is the training signal, so there is a floor. */
export const MIN_REASON = 3

export interface CorrectionView {
  correctionId: string
  ownerId: string
  entityKind: CorrectionEntityKind | string
  entityId: string
  /** what the row said on screen, so the ledger reads as English rather than as ids */
  entityLabel: string | null
  /** what Scout had said; 'suggested' when it had only offered it */
  fromValue: string | null
  toValue: string
  /** required — the reason is the training signal */
  reason: string
  at: string
  /** what re-reading the original evidence found; written by Scout, never by the person */
  investigation: string | null
  status: CorrectionStatus
  /** what the discussion concluded, typed; present only once a thread reached one */
  hypothesis: Record<string, unknown> | null
  turnCount: number
  lessonId: string | null
  ruleId: string | null
  /** the Testify ticket it was logged from, when it was one; the ledger links to /quality?ticket= */
  ticketId?: string | null
}

export interface CorrectionsPage {
  total: number
  corrections: CorrectionView[]
}

/** One turn of the "chat about why it is wrong": the person's words, or the model's reply. */
export interface Turn {
  turnId: string
  seq: number
  role: 'human' | 'model'
  text: string
  at: string
}

export interface TurnsResponse {
  turns: Turn[]
  hypothesis?: Record<string, unknown> | null
}

/** One pre-selectable correction for a kind. `hint` doubles as the quick reason behind it. */
export interface Preset {
  value: string
  label: string
  hint?: string | null
}

/** The vocabulary for one kind, server-defined so the web, the model and the loop share it. */
export interface KindPresets {
  kind: string
  label: string
  presets: Preset[]
  /** the thing a value points at when the preset carries a target ('company:' / 'parent:') */
  targetKind?: 'company' | 'person' | null
  needsEntity: boolean
}

export interface PresetsResponse {
  kinds: KindPresets[]
}

/** One correction opened: the investigation, the discussion, what the loop proposed and what it learned. */
export interface CorrectionDetail {
  correction: CorrectionView
  turns: Turn[]
  lessons: Lesson[]
  rule?: Rule | null
  proposals: Proposal[]
}

/** One thing Scout offered rather than asserted: role-aware staging held it at stage none. */
export interface Suggestion {
  entityKind: CorrectionEntityKind | string
  entityId: string
  ownerId: string
  companyId: string
  companyName: string
  companyDomain: string | null
  subject: string
  /** why it was not filed — the evidence, so the person judges that rather than a verdict */
  why: string
  /** the stage the model wanted, offered back as the one-click "put it back" */
  proposedStage: string
  proposedAmount: number | null
  occurredOn: string | null
  summary: string
}

export interface SuggestionsPage {
  total: number
  allOwners: boolean
  /** which org these suggestions answered for (?org=mine|all|<key>) */
  scope: Scope
  suggestions: Suggestion[]
}

/** A preset value that names a target it does not yet carry: 'company:' or 'parent:<companyId>'. */
export function needsTarget(preset: Preset): boolean {
  return /:$/.test(preset.value) || /<[^>]*>$/.test(preset.value)
}

/** The value to send once the person has picked the company or person the preset points at. */
export function withTarget(preset: Preset, targetId: string): string {
  return /<[^>]*>$/.test(preset.value) ? preset.value.replace(/<[^>]*>$/, targetId) : preset.value + targetId
}
